/**
 * Componente para gestionar licencias en Settings
 */

import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useLicense } from '@/hooks/use-license'
import { useLanguage } from '@/hooks/use-language'
import {
  Key,
  CheckCircle2,
  XCircle,
  Crown,
  AlertCircle,
  Calendar,
  Sparkles
} from 'lucide-react'

interface LicenseManagerProps {
  userId: string
}

export function LicenseManager({ userId }: LicenseManagerProps) {
  const { language } = useLanguage()
  const { license, licenseType, isPremium, isActive, isExpired, daysUntilExpiry, features } = useLicense(userId)

  // Badge según tipo de licencia
  const getLicenseBadge = () => {
    if (!license || !isActive) {
      return (
        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-muted text-muted-foreground text-sm">
          <XCircle className="w-4 h-4" />
          <span>{language === 'en' ? 'Free' : 'Gratuito'}</span>
        </div>
      )
    }

    if (licenseType === 'premium') {
      return (
        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium">
          <Crown className="w-4 h-4" />
          <span>{language === 'en' ? 'Premium' : 'Premium'}</span>
        </div>
      )
    }

    if (licenseType === 'enterprise') {
      return (
        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-primary to-secondary text-primary-foreground text-sm font-medium">
          <Sparkles className="w-4 h-4" />
          <span>{language === 'en' ? 'Enterprise' : 'Empresarial'}</span>
        </div>
      )
    }

    return null
  }

  return (
    <Card className="border-border/50 shadow-soft">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Key className="w-5 h-5 text-primary" />
              {language === 'en' ? 'License' : 'Licencia'}
            </CardTitle>
            <CardDescription>
              {language === 'en'
                ? 'Manage your Risko Pulse license and unlock premium features'
                : 'Gestiona tu licencia de Risko Pulse y desbloquea funciones premium'}
            </CardDescription>
          </div>
          {getLicenseBadge()}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Estado de la licencia */}
        {isActive && license && (
          <div className="space-y-4">
            {/* Features activas */}
            <div className="grid grid-cols-2 gap-3">
              <div className={`flex items-center gap-2 p-3 rounded-lg border ${features.unlimitedHistory ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-border/50 bg-muted/30'}`}>
                {features.unlimitedHistory ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-muted-foreground" />
                )}
                <span className={`text-sm ${features.unlimitedHistory ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                  {language === 'en' ? 'Unlimited History' : 'Historial Ilimitado'}
                </span>
              </div>

              <div className={`flex items-center gap-2 p-3 rounded-lg border ${features.telegramIntegration ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-border/50 bg-muted/30'}`}>
                {features.telegramIntegration ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-muted-foreground" />
                )}
                <span className={`text-sm ${features.telegramIntegration ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                  {language === 'en' ? 'Telegram Integration' : 'Integración Telegram'}
                </span>
              </div>

              <div className={`flex items-center gap-2 p-3 rounded-lg border ${features.customNotifications ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-border/50 bg-muted/30'}`}>
                {features.customNotifications ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-muted-foreground" />
                )}
                <span className={`text-sm ${features.customNotifications ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                  {language === 'en' ? 'Custom Notifications' : 'Notificaciones Custom'}
                </span>
              </div>

              <div className={`flex items-center gap-2 p-3 rounded-lg border ${features.prioritySupport ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-border/50 bg-muted/30'}`}>
                {features.prioritySupport ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-muted-foreground" />
                )}
                <span className={`text-sm ${features.prioritySupport ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                  {language === 'en' ? 'Priority Support' : 'Soporte Prioritario'}
                </span>
              </div>
            </div>

            {/* Expiración */}
            {daysUntilExpiry !== null && (
              <div className="flex items-center gap-3 p-4 rounded-lg border border-border/50 bg-muted/30">
                <Calendar className="w-5 h-5 text-muted-foreground" />
                <div>
                  <Label className="text-sm font-medium">
                    {language === 'en' ? 'License Expiry' : 'Expiración de Licencia'}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {daysUntilExpiry > 0
                      ? `${daysUntilExpiry} ${language === 'en' ? 'days remaining' : 'días restantes'}`
                      : language === 'en' ? 'Expires today' : 'Expira hoy'}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Licencia expirada */}
        {isExpired && (
          <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
            <AlertCircle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-destructive">
                {language === 'en' ? 'License Expired' : 'Licencia Expirada'}
              </p>
              <p className="text-xs text-destructive/80 mt-1">
                {language === 'en'
                  ? 'Your license has expired. Activate a new license to continue using premium features.'
                  : 'Tu licencia ha expirado. Activa una nueva licencia para continuar usando las funciones premium.'}
              </p>
            </div>
          </div>
        )}

        {/* Activar licencia */}
        {(!isActive || isExpired) && (
          <div className="space-y-4 p-4 rounded-lg border border-border/50 bg-muted/10">
            <div className="space-y-2">
              <Label className="text-base font-medium">
                {language === 'en' ? 'Manual Activation In Progress' : 'Activación Manual en Progreso'}
              </Label>
              <p className="text-xs text-muted-foreground">
                {language === 'en'
                  ? 'License activation from the app is temporarily disabled while payment and entitlement security is being hardened. New licenses are activated from the secure backend after review.'
                  : 'La activación de licencias desde la app está deshabilitada temporalmente mientras se refuerza la seguridad de pagos y permisos. Las nuevas licencias se activan desde el backend seguro tras la revisión.'}
              </p>
            </div>
          </div>
        )}

        {/* Información para usuarios free */}
        {!isPremium && (
          <div className="p-4 rounded-lg border border-primary/30 bg-primary/10">
            <p className="text-sm text-primary">
              <strong>{language === 'en' ? 'Upgrade to Premium' : 'Actualiza a Premium'}</strong>
              <br />
              {language === 'en'
                ? 'Get unlimited signal history, Telegram integration, and priority support.'
                : 'Obtén historial ilimitado de señales, integración con Telegram y soporte prioritario.'}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
